import { PiCheck } from 'react-icons/pi';
import { useState, useEffect } from 'react';
import '../webapp.css';
import { loadStripe } from '@stripe/stripe-js';
import { getAuth } from 'firebase/auth';
import { Spinner, AlertStack } from '../functions/ui';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getCurrency } from '../functions/classes';

function Pricing() {
	const [loading, setLoading] = useState(false);
	const [alerts, setAlerts] = useState([]);
	const [currency, setCurrency] = useState('CA$');
	const [premiumPrice, setPremiumPrice] = useState('CA$499');
	const [premiumFrequency, setPremiumFrequency] = useState('monthly');

	// Fetch user country and update currency/price/frequency
	useEffect(() => {
		const fetchCountry = async () => {
			const auth = getAuth();
			const user = auth.currentUser;
			if (!user) return;
			const db = getFirestore();
			const userDocRef = doc(db, 'companyUsers', user.uid);
			const userSnap = await getDoc(userDocRef);
			const symbol = userSnap.exists() ? getCurrency(userSnap.data()) : 'CA$';
			setCurrency(symbol);
			setPremiumPrice(`${symbol}499`);
			setPremiumFrequency('monthly');
		};
		fetchCountry();
	}, []);

	const plans = [
		{
			name: 'Free',
			price: currency + '0',
			frequency: premiumFrequency,
			description: 'Explore how Kierep can help you with everyday scheduling tasks',
			features: [
				'Up to 10 shifts coverage',
				'Limited KierepAI queries',
				'Up to 30 frontline staff onboarding',
			],
			button: 'Your current plan',
			isCurrent: true,
		},
		{
			name: 'Premium',
			price: premiumPrice,
			frequency: premiumFrequency,
			description: 'Level up your scheduling and revenue goals with expanded access',
			features: [
				'Unlimited shift coverage',
				'Unlimited KierepAI queries',
				'Unlimited frontline staff onboarding',
				'Premium badgeID',
				'Personalised support',
			],
			button: 'Get Premium',
			popular: true
		},
	];

	const stripePromise = loadStripe('pk_live_51RbksaKVm7YqRCzrYMQduWlhx2MDxuQSZZZVPZreMGnccPA4xSmgR8ZIKMksNGMPci4tqXuiN83AqqsCLtvqt5gR00Dx7wcsTG');

	const handleGetPremium = async () => {
		const auth = getAuth();
		const user = auth.currentUser;
		if (!user || !user.email) {
			setAlerts(prev => [...prev, { id: Date.now() + Math.random(), type: 'error', message: 'You must be logged in to upgrade' }]);
			return;
		}
		setLoading(true);
		try {
			const db = getFirestore();
			const userDocRef = doc(db, 'companyUsers', user.uid);
			const userSnap = await getDoc(userDocRef);
			const symbol = userSnap.exists() ? getCurrency(userSnap.data()) : 'CA$';
			// Use symbol to determine priceId
			// If symbol is '$', it's USA; if 'CA$', it's Canada
			const priceId = symbol === '$' ? 'price_1RePJYKVm7YqRCzrWjZxoZaN' : 'price_1S3ijQKVm7YqRCzrNdLFiGTJ';
			const response = await fetch('https://capitalbeaverserver.vercel.app/api/stripe', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					priceId,
					email: user.email,
					userId: user.uid,
				}),
			});
			const { id } = await response.json();
			const stripe = await stripePromise;
			await stripe.redirectToCheckout({ sessionId: id });
		} catch (err) {
			setAlerts(prev => [...prev, { id: Date.now() + Math.random(), type: 'error', message: 'There was an error starting the checkout. Please try again' }]);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="pricing-wrapper">
			<h1>Upgrade your plan</h1>
			<div className="plans">
				{plans.map((plan, idx) => (
					<div
						key={idx}
						className={`plan-box ${plan.popular ? 'popular' : ''} ${plan.isCurrent ? 'current' : ''}`}
					>
						<div className="plan-header">
							<div>
								<h3>{plan.name}</h3>
								{plan.popular && <span className="tag">POPULAR</span>}
							</div>
							<p className="price">
								{plan.price}<span>{plan.frequency}</span>
							</p>
						</div>
						<p className="plan-desc">{plan.description}</p>
						<button
							className={`plan-button ${plan.isCurrent ? 'disabled' : ''}`}
							onClick={plan.name === 'Premium' && !loading ? handleGetPremium : undefined}
							disabled={plan.isCurrent || loading}
						>
							{plan.isCurrent
								? plan.button
								: loading && plan.name === 'Premium'
									? <Spinner />
									: plan.button}
						</button>
						<ul>
							{plan.features.map((feature, i) => (
								<li key={i}><PiCheck/>{feature}</li>
							))}
						</ul>
					</div>
				))}
			</div>
			{/* Alert Box */}
			<Spinner />
			<AlertStack alerts={alerts} onAlertClose={id => setAlerts(prev => prev.filter(a => a.id !== id))} />
		</div>
	);
}

export default Pricing;